/**
 * Timezone-aware date utilities.
 *
 * The app stores due dates as midnight UTC (e.g. 2025-01-28T00:00:00Z).
 * When determining "today", we must use the user's timezone so that
 * at 8 PM Pacific on Jan 28, "today" is still Jan 28 (not Jan 29 UTC).
 */

const DEFAULT_TIMEZONE = "America/Los_Angeles";

let _configuredTimezone: string = DEFAULT_TIMEZONE;

/** Set the app-wide timezone (called when settings are loaded). */
export function setAppTimezone(tz: string): void {
  _configuredTimezone = tz;
  console.log("[dateUtils] Timezone set to:", tz);
}

/** Get the currently configured timezone. */
export function getAppTimezone(): string {
  return _configuredTimezone;
}

/**
 * Get "today" as a midnight-UTC Date, but based on the user's timezone.
 *
 * Example: At 8 PM Pacific on Jan 28 (which is 4 AM UTC Jan 29),
 * this returns Date representing 2025-01-28T00:00:00Z (midnight UTC Jan 28).
 */
export function getTodayLocal(timezone?: string): Date {
  const tz = timezone || _configuredTimezone;
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(now);

  const year = parseInt(parts.find((p) => p.type === "year")!.value);
  const month = parseInt(parts.find((p) => p.type === "month")!.value) - 1;
  const day = parseInt(parts.find((p) => p.type === "day")!.value);

  return new Date(Date.UTC(year, month, day));
}

/**
 * Check if a given date is "today" in the user's timezone.
 */
export function isTodayLocal(date: Date | null | undefined): boolean {
  if (!date || !(date instanceof Date)) return false;
  const today = getTodayLocal();
  return (
    date.getUTCFullYear() === today.getUTCFullYear() &&
    date.getUTCMonth() === today.getUTCMonth() &&
    date.getUTCDate() === today.getUTCDate()
  );
}

/**
 * Check if a given date is "tomorrow" in the user's timezone.
 */
export function isTomorrowLocal(date: Date | null | undefined): boolean {
  if (!date || !(date instanceof Date)) return false;
  const today = getTodayLocal();
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  return (
    date.getUTCFullYear() === tomorrow.getUTCFullYear() &&
    date.getUTCMonth() === tomorrow.getUTCMonth() &&
    date.getUTCDate() === tomorrow.getUTCDate()
  );
}

/**
 * Normalize a Date to midnight UTC using its UTC components.
 * Use this for stored dates (due dates, etc.) that are already in UTC.
 */
export function toMidnightUTC(date: Date): Date {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
}

/**
 * Format a UTC-midnight Date for display without timezone shift.
 *
 * Dates in this app are stored as midnight UTC (e.g. 2026-02-01T00:00:00Z).
 * Using toLocaleDateString() without specifying timeZone: "UTC" will shift
 * the date backwards for users west of UTC (e.g. 2/1 becomes 1/31 in Pacific).
 *
 * This function safely formats using the UTC date components.
 */
export function formatDateUTC(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Format an actual event timestamp (e.g. a Plaid last-sync time) as a long,
 * human-readable string in the viewer's local timezone, e.g.
 * "Monday January 1st, 2026 at 1:01 PM". Returns "" for invalid input.
 */
export function formatImportedAt(
  date: Date | string | null | undefined
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
  const month = d.toLocaleDateString("en-US", { month: "long" });
  const day = d.getDate();
  const ordinal = (n: number): string => {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1:
        return `${n}st`;
      case 2:
        return `${n}nd`;
      case 3:
        return `${n}rd`;
      default:
        return `${n}th`;
    }
  };
  const year = d.getFullYear();
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${weekday} ${month} ${ordinal(day)}, ${year} at ${time}`;
}

export function toLocalDatetimeInputValue(date: Date): string {
  const pad = (value: number) => value.toString().padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
